import type { ReactNode } from "react";
import { Card, cn } from "@playbookai/ui";

export interface KpiPanelProps {
  readonly title: string;
  /**
   * Makes the TITLE activate this, instead of adding a second control that
   * names the same destination.
   *
   * The heading stays a heading: the button is rendered INSIDE the `<h2>`, so
   * `aria-labelledby` still resolves to the same text and a screen reader still
   * announces the card by its name — it simply also announces that the name is
   * pressable. Making the `<h2>` itself clickable would take the heading out of
   * the document outline, which is the usual way this pattern goes wrong.
   *
   * It carries no icon and no link colour: the card title should still read as
   * the card's title, and underline it on hover rather than dressing it up as a
   * second control.
   */
  readonly onTitleActivate?: (() => void) | undefined;
  readonly subtitle?: string;
  /** Controls at the trailing edge of the header (the canvas' card action). */
  readonly action?: ReactNode;
  readonly headingId?: string;
  readonly className?: string;
  readonly children: ReactNode;
}

/** A KPI page card as the Direction B canvas draws it (PB-5134, d37): the DS
 * Card surface with a titled header, an optional one-line subtitle and an
 * action slot. Still a named region — the Card is a div, so the landmark the
 * `<section>` used to give is restated as `role="region"`. */
// Kept hand-built (not CardHeader/CardBody, not Button): CardHeader's title
// is a fixed text-sm, not this panel's text-heading-3; and Button is a boxed
// control (always sized/padded) while this title trigger is plain inline
// text with an underline hover, never a button-shaped box. Neither move is
// an approved T01 look row.
export function KpiPanel({
  title,
  onTitleActivate,
  subtitle,
  action,
  headingId,
  className,
  children,
}: KpiPanelProps): JSX.Element {
  return (
    <Card
      role={headingId ? "region" : undefined}
      aria-labelledby={headingId}
      className={cn("px-5 py-4.5", className)}
    >
      <div className="mb-3.75 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2
            id={headingId}
            className="text-heading-3 tracking-tight text-pb-text"
          >
            {onTitleActivate ? (
              <button
                type="button"
                onClick={onTitleActivate}
                className="focus-ring rounded-sm text-start text-pb-text hover:underline"
              >
                {title}
              </button>
            ) : (
              title
            )}
          </h2>
          {subtitle ? (
            <p className="mt-0.5 text-caption text-pb-text-muted">{subtitle}</p>
          ) : null}
        </div>
        {action ? (
          <div className="flex flex-wrap items-center gap-2">{action}</div>
        ) : null}
      </div>
      {children}
    </Card>
  );
}
